import { useCallback, useMemo } from 'react'
import {
  DealTableRow,
  GenericSortHeader,
  dealTableHeadRowClass,
  useDealTableInteraction,
  useGenericTableSort,
} from '@/components/shared/DealTable'
import {
  AlertTriangle,
  Shield,
  Eye,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react'
import { usePortfolioAnalyticsBook } from '@/hooks/useDemoDataAccess'

const pct = (v: number, d = 1) => `${(v * 100).toFixed(d)}%`

type RiskTier = 'Low' | 'Medium' | 'High'

interface DealRisk {
  id: string
  property: string
  type: string
  riskTier: RiskTier
  priorTier: RiskTier
  ltvProximity: number   // how close to 75% covenant
  dscrProximity: number  // how close to 1.25x covenant
  triggerCount: number
  overdueTriggers: number
}

function tierColor(tier: RiskTier): string {
  return tier === 'Low' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' :
    tier === 'Medium' ? 'text-amber-400 bg-amber-400/10 border-amber-400/20' :
    'text-red-400 bg-red-400/10 border-red-400/20'
}

function migrationArrow(current: RiskTier, prior: RiskTier) {
  const order: Record<RiskTier, number> = { Low: 0, Medium: 1, High: 2 }
  if (order[current] > order[prior]) return <ArrowUpRight size={11} className="text-red-400" />
  if (order[current] < order[prior]) return <ArrowDownRight size={11} className="text-emerald-400" />
  return <Minus size={11} className="text-stone-600" />
}

// Key Risk Indicators
interface KRI {
  label: string
  value: string
  threshold: string
  status: 'ok' | 'warning' | 'breach'
  trend: 'up' | 'down' | 'flat'
}

const RISK_TIER_ORDER: Record<RiskTier, number> = { High: 0, Medium: 1, Low: 2 }

export default function RiskMonitoringDashboard() {
  const deals = usePortfolioAnalyticsBook().complete
  const { focusedDealId, activateDeal } = useDealTableInteraction()

  const riskData = useMemo((): DealRisk[] => {
    return deals.map((d) => {
      const ltv = d.ltv
      const dscr = d.dscr
      const ltvProximity = (0.75 - ltv) / 0.75 // how far from breach (positive = safe)
      const dscrProximity = (dscr - 1.25) / 1.25

      let riskTier: RiskTier = 'Low'
      if (ltvProximity < 0 || dscrProximity < 0) riskTier = 'High'
      else if (ltvProximity < 0.10 || dscrProximity < 0.10) riskTier = 'Medium'

      // Register I7. This drifted the tier with Math.random() over LIVE deals,
      // so a real loan appeared to have moved between risk tiers — differently
      // on every render. There is no stored prior tier to compare against, so
      // the honest answer is that it has not been observed to move.
      const priorTier: RiskTier = riskTier

      return {
        id: d.id,
        property: d.property,
        type: d.type,
        riskTier,
        priorTier,
        ltvProximity,
        dscrProximity,
        triggerCount: d.triggers.length,
        overdueTriggers: d.triggers.filter((t) => t.status === 'overdue').length,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getRiskRowValue = useCallback((row: DealRisk, columnIndex: number) => {
    switch (columnIndex) {
      case 0:
        return row.property
      case 1:
        return row.type
      case 2:
        return RISK_TIER_ORDER[row.riskTier]
      case 3:
        return RISK_TIER_ORDER[row.riskTier] - RISK_TIER_ORDER[row.priorTier]
      case 4:
        return row.ltvProximity
      case 5:
        return row.dscrProximity
      case 6:
        return row.triggerCount
      case 7:
        return row.overdueTriggers
      default:
        return ''
    }
  }, [])

  const { sortColumn, sortDir, toggleSort, sortedRows: sortedRiskData } = useGenericTableSort(
    riskData,
    getRiskRowValue,
    2,
  )

  const lowCount = riskData.filter((d) => d.riskTier === 'Low').length
  const medCount = riskData.filter((d) => d.riskTier === 'Medium').length
  const highCount = riskData.filter((d) => d.riskTier === 'High').length
  const watchlistCount = riskData.filter((d) => d.riskTier !== 'Low').length
  const overdueTriggers = riskData.reduce((s, d) => s + d.overdueTriggers, 0)
  const earlyWarning = riskData.filter((d) => d.riskTier === 'Low' && (d.ltvProximity < 0.15 || d.dscrProximity < 0.15)).length

  // Migration matrix
  const matrix = useMemo(() => {
    const tiers: RiskTier[] = ['Low', 'Medium', 'High']
    const m: number[][] = [[0, 0, 0], [0, 0, 0], [0, 0, 0]]
    riskData.forEach((d) => {
      m[tiers.indexOf(d.priorTier)][tiers.indexOf(d.riskTier)]++
    })
    return m
  }, [riskData])

  // KRIs
  const kris: KRI[] = [
    {
      label: 'Wtd Avg LTV',
      value: pct(deals.reduce((s, d) => s + d.ltv * d.loanAmount, 0) / deals.reduce((s, d) => s + d.loanAmount, 0)),
      threshold: '< 75%',
      status: 'ok',
      trend: 'flat',
    },
    {
      label: 'Wtd Avg DSCR',
      value: `${(deals.reduce((s, d) => s + d.dscr * d.loanAmount, 0) / deals.reduce((s, d) => s + d.loanAmount, 0)).toFixed(2)}x`,
      threshold: '> 1.25x',
      status: 'ok',
      trend: 'up',
    },
    {
      label: 'Overdue Triggers',
      value: String(overdueTriggers),
      threshold: '0',
      status: overdueTriggers > 0 ? 'breach' : 'ok',
      trend: overdueTriggers > 0 ? 'up' : 'flat',
    },
    {
      label: 'Covenant Breaches',
      value: String(highCount),
      threshold: '0',
      status: highCount > 0 ? 'warning' : 'ok',
      trend: 'flat',
    },
    {
      label: 'Watchlist Deals',
      value: String(watchlistCount),
      threshold: '< 20%',
      status: watchlistCount / deals.length > 0.20 ? 'warning' : 'ok',
      trend: 'flat',
    },
    {
      label: 'Early Warnings',
      value: String(earlyWarning),
      threshold: '< 3',
      status: earlyWarning >= 3 ? 'warning' : 'ok',
      trend: earlyWarning > 0 ? 'up' : 'flat',
    },
  ]

  return (
    <div className="space-y-4">
      {/* Risk Summary */}
      <div className="grid grid-cols-4 gap-3">
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-emerald-400/10"><Shield size={14} className="text-emerald-400" /></div>
          <div>
            <div className="metric-cell-label">Low Risk</div>
            <div className="metric-cell-value text-emerald-400">{lowCount}</div>
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-amber-400/10"><Eye size={14} className="text-amber-400" /></div>
          <div>
            <div className="metric-cell-label">Medium Risk</div>
            <div className="metric-cell-value text-amber-400">{medCount}</div>
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-red-400/10"><AlertTriangle size={14} className="text-red-400" /></div>
          <div>
            <div className="metric-cell-label">High Risk</div>
            <div className="metric-cell-value text-red-400">{highCount}</div>
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-amber-400/10"><TrendingDown size={14} className="text-amber-300" /></div>
          <div>
            <div className="metric-cell-label">Early Warnings</div>
            <div className="metric-cell-value text-amber-300">{earlyWarning}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* KRI Cards */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Key Risk Indicators</span>
          </div>
          <div className="panel-body space-y-2">
            {kris.map((kri) => (
              <div key={kri.label} className="flex items-center gap-3 rounded border border-amber-400/5 bg-black/20 px-3 py-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  kri.status === 'ok' ? 'bg-emerald-400' : kri.status === 'warning' ? 'bg-amber-400' : 'bg-red-400'
                }`} />
                <span className="flex-1 text-[11px] text-stone-300">{kri.label}</span>
                <span className="text-[12px] font-mono font-semibold text-stone-100">{kri.value}</span>
                <span className="text-[9px] text-stone-600 font-mono">{kri.threshold}</span>
                {kri.trend === 'up' && <ArrowUpRight size={10} className="text-red-400" />}
                {kri.trend === 'down' && <ArrowDownRight size={10} className="text-emerald-400" />}
                {kri.trend === 'flat' && <Minus size={10} className="text-stone-600" />}
              </div>
            ))}
          </div>
        </div>

        {/* Migration Matrix */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Risk Migration Matrix</span>
            <span className="text-[10px] text-stone-600 font-mono">Prior → Current</span>
          </div>
          <div className="panel-body">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>From ↓ / To →</th>
                  <th className="text-center text-emerald-400">Low</th>
                  <th className="text-center text-amber-400">Medium</th>
                  <th className="text-center text-red-400">High</th>
                </tr>
              </thead>
              <tbody>
                {(['Low', 'Medium', 'High'] as RiskTier[]).map((tier, i) => (
                  <tr key={tier}>
                    <td className={`font-medium ${tierColor(tier).split(' ')[0]}`}>{tier}</td>
                    {matrix[i].map((count, j) => (
                      <td key={j} className={`text-center font-mono ${
                        i === j ? 'text-stone-300' : j > i ? 'text-red-400' : 'text-emerald-400'
                      } ${count > 0 && i !== j ? 'font-bold' : ''}`}>
                        {count}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Deal-level Risk Table */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Deal Risk Assessment</span>
          <span className="text-[10px] font-mono text-stone-600">{riskData.length} deals</span>
        </div>
        <div className="overflow-x-auto">
          <table className="dense-table">
            <thead>
              <tr className={dealTableHeadRowClass()}>
                <GenericSortHeader label="Property" columnIndex={0} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Type" columnIndex={1} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Risk Tier" columnIndex={2} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="center" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Migration" columnIndex={3} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="center" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="LTV Headroom" columnIndex={4} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="DSCR Cushion" columnIndex={5} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Triggers" columnIndex={6} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Overdue" columnIndex={7} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
              </tr>
            </thead>
            <tbody>
              {sortedRiskData.map((d) => (
                <DealTableRow
                  key={d.id}
                  dealId={d.id}
                  isFocused={focusedDealId === d.id}
                  onActivate={activateDeal}
                  openProfileOnClick={false}
                  className={d.riskTier === 'High' ? 'bg-red-400/[0.03]' : ''}
                >
                  <td className="font-medium text-stone-200 whitespace-nowrap">{d.property}</td>
                  <td className="text-stone-400">{d.type}</td>
                  <td className="text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${tierColor(d.riskTier)}`}>
                      {d.riskTier}
                    </span>
                  </td>
                  <td className="text-center">{migrationArrow(d.riskTier, d.priorTier)}</td>
                  <td className={`text-right font-mono ${d.ltvProximity < 0.10 ? 'text-red-400' : d.ltvProximity < 0.20 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {d.ltvProximity >= 0 ? `+${pct(d.ltvProximity)}` : pct(d.ltvProximity)}
                  </td>
                  <td className={`text-right font-mono ${d.dscrProximity < 0.10 ? 'text-red-400' : d.dscrProximity < 0.20 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {d.dscrProximity >= 0 ? `+${pct(d.dscrProximity)}` : pct(d.dscrProximity)}
                  </td>
                  <td className="text-right font-mono">{d.triggerCount}</td>
                  <td className={`text-right font-mono ${d.overdueTriggers > 0 ? 'text-red-400 font-bold' : 'text-stone-400'}`}>
                    {d.overdueTriggers}
                  </td>
                </DealTableRow>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
